'use client';

import { FunctionComponent, MouseEvent, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useStoreMap, useUnit } from 'effector-react';
import {
  $menuWorkspaceId,
  getRootMenuDropAreaId,
  setActiveItemIdEvent,
  toggleActiveItemIdEvent,
  toggleExpandItemFx,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $members } from '@nx-frontend/shared/store/org-members';
import { $usersState } from '@nx-frontend/shared/store/users-store';
import { $workspaceMemberUserIds } from '@nx-frontend/shared/store/workspace-members';
import {
  MenuItem,
  MenuItemType,
  MenuTooltipMap,
  MenuType,
  OrgMemberRole,
  UserWithRoleAndAccess,
} from '@nx-frontend/shared/types';
import { MenuTreeItem } from './menu-tree-item';
import { MenuTreeItemDraggable } from './menu-tree-item-draggable';
import { MenuTreeItemDroppable } from './menu-tree-item-droppable';
import { MenuTreeItemEmpty } from './menu-tree-item-empty';
import { getIsMenuPermissionsTouchedParams, getMenuActiveItemIdsParams, rootOrderIdsParams } from './store-maps';

export interface MenuTreeProps {
  menuType: MenuType;
  disabledDragItemTypes?: MenuItemType[];
  disabledDropItemTypes?: MenuItemType[];
  hideAddButtonItemTypes?: MenuItemType[];
  hideMenuButtonItemTypes?: MenuItemType[];
  showOpenInNewTabItemTypes?: MenuItemType[];
  allowClickIconItemTypes?: MenuItemType[];
  disabledSelectMenuItemTypes?: MenuItemType[];
  excludeIds?: string[];
  multiSelectEnabled?: boolean;
  multiSelectMaxItems?: number;
  emptyFolderText?: string;
  selectDisabled?: boolean;
  tooltipMap?: MenuTooltipMap;
  activeDragId?: string;
  activeHighlightDisabled?: boolean;
  initMenuItemLevel?: number;
  onMenuItemToggle?: (menuItem: MenuItem) => void;
  onMenuItemClick?: (menuItem: MenuItem) => boolean | void;
  onMenuItemHover?: (menuItem: MenuItem) => void;
  onMenuItemMore?: (menuItem: MenuItem | undefined, event: MouseEvent) => void;
  onMenuItemAdd?: (menuItem: MenuItem | undefined, event: MouseEvent) => void;
  onMenuItemOpenInNewTab?: (item: MenuItem) => void;
  onDragOver?: (id: string) => void;
  onMenuItemIcon?: (menuItem: MenuItem | undefined, event: MouseEvent) => void;
  renderers?: Record<string, FunctionComponent<MenuItem>> | undefined;
  customClasses?: Record<number, string> | undefined;
  onMenuItemRightClick?: (menuItem: MenuItem | undefined, event: MouseEvent) => void;
  permissionPreviewEnabled?: boolean;
  onPermissionClick?: (item: MenuItem) => void;
  userId?: number;
  availablePortalOrgRoles?: OrgMemberRole[];
  availableConsoleOrgRoles?: OrgMemberRole[];
  /**
   * limits number of displayed children items
   * truncates long children lists and displays a "Show more" button
   */
  maxChildrenItemsToRender?: number;
  /**
   * Called when user clicks on "Show more..." button when children are truncated
   * @param menuItem - parent item which children are truncated
   */
  onMoreItemsClick?: (menuItem: MenuItem) => void;
  forceActiveId?: string;
  hideRootEmptyMessage?: boolean;
}

export const sessionStorageExpandedMenuKey = 'menu-expanded-types';
export function MenuTree({
  menuType,
  disabledDragItemTypes = [],
  disabledDropItemTypes = [],
  hideAddButtonItemTypes = [],
  hideMenuButtonItemTypes = [],
  showOpenInNewTabItemTypes = [],
  allowClickIconItemTypes = [],
  excludeIds = [],
  multiSelectEnabled = false,
  multiSelectMaxItems,
  disabledSelectMenuItemTypes,
  emptyFolderText,
  selectDisabled = false,
  tooltipMap,
  activeDragId,
  activeHighlightDisabled = false,
  initMenuItemLevel = 0,
  onMenuItemToggle,
  onMenuItemClick,
  onMenuItemHover,
  onMenuItemMore,
  onMenuItemAdd,
  onMenuItemOpenInNewTab,
  onDragOver,
  onMenuItemIcon,
  renderers,
  customClasses,
  onMenuItemRightClick,
  permissionPreviewEnabled = false,
  onPermissionClick,
  userId,
  availablePortalOrgRoles,
  availableConsoleOrgRoles,
  maxChildrenItemsToRender,
  onMoreItemsClick,
  forceActiveId,
  hideRootEmptyMessage,
}: MenuTreeProps) {
  const rootOrderIds = useStoreMap(rootOrderIdsParams(menuType));
  const touchedPermissions = useStoreMap(getIsMenuPermissionsTouchedParams(menuType));
  const activeItemIds = useStoreMap(getMenuActiveItemIdsParams(menuType));
  const [toggleExpandItem, setActiveItemId, toggleActiveItemId] = useEvent([
    toggleExpandItemFx,
    setActiveItemIdEvent,
    toggleActiveItemIdEvent,
  ]);
  const [workspaceId, members, userEntities, workspaceMemberIds] = useUnit([
    $menuWorkspaceId,
    $members,
    $usersState.map((state) => state.entities),
    $workspaceMemberUserIds,
  ]);
  const t = useTranslations('webnotes');

  emptyFolderText = emptyFolderText ?? t('webnotes-no_content_yet');

  const usersWithRole = members.reduce((acc, member) => {
    const user = userEntities[member.userId];
    if (user && workspaceMemberIds.includes(user.id)) {
      acc.push({
        ...user,
        role: member.role,
        hasPortalAccess: availablePortalOrgRoles && availablePortalOrgRoles.includes(member.role),
        hasPortalConsoleAccess: availableConsoleOrgRoles && availableConsoleOrgRoles.includes(member.role),
      });
    }
    return acc;
  }, [] as UserWithRoleAndAccess[]);

  const handleMenuItemToggle = useCallback(
    (item: MenuItem) => {
      if (onMenuItemToggle) {
        onMenuItemToggle(item);
      }
      toggleExpandItem({ item, menuType, workspaceId });
    },
    [menuType, workspaceId, toggleExpandItem, onMenuItemToggle]
  );

  const handleMenuItemClick = useCallback(
    (item: MenuItem) => {
      if (onMenuItemClick) {
        const continueFn = onMenuItemClick(item);
        if (typeof continueFn === 'boolean' && !continueFn) {
          return;
        }
      }

      const itemSelectDisabled = disabledSelectMenuItemTypes && disabledSelectMenuItemTypes.includes(item.type);
      if (!selectDisabled && !itemSelectDisabled) {
        if (multiSelectEnabled) {
          if (
            !(
              typeof multiSelectMaxItems === 'number' &&
              !activeItemIds.includes(item.id) &&
              activeItemIds.length >= multiSelectMaxItems
            )
          ) {
            toggleActiveItemId({ id: item.id, menuType });
          }
        } else {
          setActiveItemId({ id: item.id, menuType });
        }
      }
    },
    [
      menuType,
      onMenuItemClick,
      multiSelectEnabled,
      selectDisabled,
      setActiveItemId,
      toggleActiveItemId,
      disabledSelectMenuItemTypes,
      activeItemIds,
      multiSelectMaxItems,
    ]
  );

  const handleMenuItemHover = useCallback(
    (item: MenuItem) => {
      if (onMenuItemHover) {
        onMenuItemHover(item);
      }
    },
    [onMenuItemHover]
  );

  const handleMoreClick = useCallback(
    (item: MenuItem | undefined, event: MouseEvent) => {
      if (onMenuItemMore) {
        onMenuItemMore(item, event);
      }
    },
    [onMenuItemMore]
  );

  const handleAddClick = useCallback(
    (item: MenuItem | undefined, event: MouseEvent) => {
      if (onMenuItemAdd) {
        onMenuItemAdd(item, event);
      }
    },
    [onMenuItemAdd]
  );

  const handleIconClick = useCallback(
    (item: MenuItem | undefined, event: MouseEvent) => {
      if (onMenuItemIcon) {
        onMenuItemIcon(item, event);
      }
    },
    [onMenuItemIcon]
  );

  const handleMenuItemRightClick = useCallback(
    (item: MenuItem | undefined, event: MouseEvent) => {
      if (onMenuItemRightClick) {
        onMenuItemRightClick(item, event);
      }
    },
    [onMenuItemRightClick]
  );

  const rootDropId = useMemo(() => {
    return getRootMenuDropAreaId(menuType);
  }, [menuType]);

  return (
    <div>
      {rootOrderIds.map((id) => {
        return (
          <MenuTreeItemDraggable
            id={id}
            key={`draggable-${id}`}
            menuType={menuType}
            disabledDragItemTypes={disabledDragItemTypes}
          >
            <MenuTreeItemDroppable
              id={id}
              key={`droppable-${id}`}
              level={initMenuItemLevel}
              activeDragId={activeDragId}
              menuType={menuType}
              onDragOver={() => (onDragOver ? onDragOver(id) : false)}
              disabledDropItemTypes={disabledDropItemTypes}
            >
              <MenuTreeItem
                id={id}
                key={id}
                menuType={menuType}
                level={initMenuItemLevel}
                excludeIds={excludeIds}
                disabledDragItemTypes={disabledDragItemTypes}
                disabledDropItemTypes={disabledDropItemTypes}
                hideAddButtonItemTypes={hideAddButtonItemTypes}
                hideMenuButtonItemTypes={hideMenuButtonItemTypes}
                showOpenInNewTabItemTypes={showOpenInNewTabItemTypes}
                allowClickIconItemTypes={allowClickIconItemTypes}
                multiSelectEnabled={multiSelectEnabled}
                emptyFolderText={emptyFolderText}
                tooltipMap={tooltipMap}
                activeDragId={activeDragId}
                activeHighlightDisabled={activeHighlightDisabled}
                onMenuItemToggle={handleMenuItemToggle}
                onMenuItemClick={handleMenuItemClick}
                onMenuItemHover={handleMenuItemHover}
                onMoreClick={handleMoreClick}
                onAddClick={handleAddClick}
                onMenuItemOpenInNewTab={onMenuItemOpenInNewTab}
                onIconClick={handleIconClick}
                customRenderers={renderers}
                customClasses={customClasses}
                onDragOver={onDragOver}
                onMenuItemRightClick={handleMenuItemRightClick}
                permissionPreviewEnabled={permissionPreviewEnabled && touchedPermissions}
                onPermissionClick={onPermissionClick}
                usersWithRole={usersWithRole}
                userId={userId}
                maxChildrenItemsToRender={maxChildrenItemsToRender}
                onMoreItemsClick={onMoreItemsClick}
                forceActiveId={forceActiveId}
              />
            </MenuTreeItemDroppable>
          </MenuTreeItemDraggable>
        );
      })}
      {!rootOrderIds.length && (
        <MenuTreeItemDroppable
          id={rootDropId}
          key={`droppable-${rootDropId}`}
          level={initMenuItemLevel}
          isEmpty={true}
          activeDragId={activeDragId}
          menuType={menuType}
          hideBottomDropArea={true}
          onDragOver={() => (onDragOver ? onDragOver(rootDropId) : false)}
        >
          <div>
            {!hideRootEmptyMessage && (
              <MenuTreeItemEmpty
                level={initMenuItemLevel}
                onMenuItemAdd={(event) => handleAddClick(undefined, event)}
                permissionPreviewEnabled={permissionPreviewEnabled}
              />
            )}
          </div>
        </MenuTreeItemDroppable>
      )}
    </div>
  );
}

export default MenuTree;
